/**
 * D07 — Distribution Detail: when the meals go out, and why.
 *
 * The purpose options and the bookable date window come from the server. When
 * a purpose needs free text ("In Memory of Someone", "Other") an input appears
 * — the artwork never designed one, though the field is required (gap G-04).
 */

import { useState } from 'react';
import { StyleSheet, TextInput, View } from 'react-native';

import {
  formatDate,
  toISODate,
  validateDistributionDate,
  validatePurpose,
} from '@mfa/domain';
import { colors, radius, space, textStyles } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { CheckoutHeader } from '../../components/donation/CheckoutHeader';
import { RadioGroup } from '../../components/forms/Choice';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Text } from '../../components/common/Text';
import { useAppConfig } from '../../core/api/hooks';
import { useDonationStore, type PurposeCode } from '../donation/donationStore';
import { DatePickerField } from '../../components/forms/DatePickerField';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

export function OrderDetailsScreen({ navigation }: DonorScreenProps<'OrderDetails'>) {
  const t = useT();
  const { data: config } = useAppConfig();

  const purpose = useDonationStore((s) => s.purpose);
  const purposeDetails = useDonationStore((s) => s.purposeDetails);
  const distributionDate = useDonationStore((s) => s.distributionDate);
  const setPurpose = useDonationStore((s) => s.setPurpose);
  const setDistributionDate = useDonationStore((s) => s.setDistributionDate);

  const [touched, setTouched] = useState(false);

  const selectedOption = config?.purposes.find((p) => p.code === purpose);
  const needsDetails = selectedOption?.requiresDetails ?? false;

  const purposeError =
    touched && config && purpose ? validatePurpose(purpose, purposeDetails, config) : null;
  const dateError =
    touched && config ? validateDistributionDate(distributionDate ?? '', config) : null;

  const canContinue = Boolean(purpose && distributionDate && !purposeError && !dateError);

  const onContinue = () => {
    setTouched(true);
    if (!config || !purpose || !distributionDate) return;
    if (validatePurpose(purpose, purposeDetails, config)) return;
    if (validateDistributionDate(distributionDate, config)) return;
    navigation.navigate('DonorInformation');
  };

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-order-details"
      subHeader={<CheckoutHeader current={0} />}
      pinnedFooter={
        <Button
          label={t('Continue to Donor Info')}
          onPress={onContinue}
          disabled={!canContinue}
          testID="cta-order-continue"
        />
      }
    >
      <Card title={t('Distribution Date')}>
        <DatePickerField
          value={distributionDate}
          onChange={setDistributionDate}
          today={config?.distributionDate.today}
          minDaysAhead={config?.distributionDate.minDaysAhead ?? 1}
          maxDaysAhead={config?.distributionDate.maxDaysAhead ?? 90}
          blackoutDates={config?.distributionDate.blackoutDates ?? []}
          error={dateError}
        />
        {distributionDate && !dateError ? (
          <Text variant="caption" tone="tertiary" style={styles.dateHint}>
            Meals will be distributed on {formatDate(distributionDate)}.
          </Text>
        ) : null}
      </Card>

      <Card title={t('Purpose of Donation')} style={styles.card}>
        <RadioGroup<PurposeCode>
          testID="purpose-group"
          options={(config?.purposes ?? []).map((option) => ({
            value: option.code,
            label: t(option.label),
          }))}
          value={purpose}
          onChange={(code) => setPurpose(code, purposeDetails)}
          error={purposeError}
        />

        {needsDetails ? (
          <View style={styles.detailsPanel}>
            <Text variant="label" tone="secondary">{t('Tell us a little more')}</Text>
            <TextInput
              testID="purpose-details"
              value={purposeDetails ?? ''}
              onChangeText={(value) => purpose && setPurpose(purpose, value)}
              placeholder={t('In memory of…')}
              placeholderTextColor={colors.text.disabled}
              style={styles.detailsInput}
              multiline
              maxLength={280}
              accessibilityLabel={t('Purpose details')}
            />
          </View>
        ) : null}
      </Card>

      <Text variant="caption" tone="tertiary" style={styles.note}>
        {t('Today is {date}.').replace('{date}', formatDate(toISODate(new Date())))}
      </Text>
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  card: {
    marginTop: space.lg,
  },
  detailsPanel: {
    marginTop: space.md,
    gap: space.xs,
  },
  detailsInput: {
    borderWidth: 1,
    borderColor: colors.border.default,
    borderRadius: radius.md,
    padding: space.md,
    minHeight: 80,
    textAlignVertical: 'top',
    color: colors.text.primary,
    fontSize: textStyles.body.fontSize,
  },
  dateHint: {
    marginTop: space.sm,
  },
  note: {
    marginTop: space.lg,
    textAlign: 'center',
  },
});
